import { isRemoteEnabled, RemoteConfig } from 'mfe/runtime';
import { getOrganizationUser } from 'utils/authTyped';

/**
 * Inventory microfrontend descriptor (`parrot-inventory-web`).
 * Enabled per environment via `REACT_APP_INVENTORY_MFE_URL`; QA can point any
 * shell environment at any remote deploy with `?inventoryRemote=<url>`.
 */
export const inventoryRemote: RemoteConfig = {
  domain: 'INV',
  envUrl: process.env.REACT_APP_INVENTORY_MFE_URL,
  name: 'inventory',
  overrideParam: 'inventoryRemote',
};

/**
 * Whether this organization was moved to the V2 inventories service.
 *
 * Source of truth is the backend: `Organization.use_inventories_v2` (RFC-INV-001),
 * which travels in the `organization` block of `/api/v1/auth/profile/` and of the
 * login payload, and is persisted verbatim by `setOrganizationUser`.
 *
 * `=== true` and not a truthy check on purpose: the `organization` block is absent
 * from the payload when the user has no organization and no stores, and a session
 * stored before this field existed has no key either. Both mean V1, which is the
 * safe default — every org starts on V1 and is moved one by one.
 *
 * Read on every call rather than captured once: the value is refreshed when the
 * shell re-fetches the profile, and a cached copy would keep routing to the old
 * module for the rest of the session.
 */
export const usesInventoriesV2 = (): boolean => getOrganizationUser()?.useInventoriesV2 === true;

/**
 * Whether the inventory microfrontend should replace the legacy
 * inventories routes for this session.
 *
 * Two independent axes, both required:
 *   - `isRemoteEnabled` — is the remote deployed and reachable in this environment?
 *     Infrastructure readiness, set per deploy.
 *   - `usesInventoriesV2` — was this tenant migrated to V2? Business rollout, flipped
 *     per organization at runtime from the internal endpoint or the Django admin,
 *     without a deploy.
 *
 * The remote talks to `parrot-inventory-api-v2`, so serving it to an organization
 * still on V1 would point the UI at a service that does not hold its stock.
 */
export const isInventoryMfeEnabled = (): boolean => isRemoteEnabled(inventoryRemote) && usesInventoriesV2();
